import isEqual from 'lodash.isequal';
import { FxRateKey, IInstrumentQuote, InstrumentCode, QuoteKey } from './types';
import { LocationType } from '@cmctechnology/webinvest-store-frontend';
import { useDispatch, useSelector } from 'react-redux';
import { FxRates, IStore, MARKET_DATA_STORE_NAME } from './store';
import { useEffect, useState } from 'react';
import { subscribeQuote, unsubscribeQuote } from './quoteActions';
import { subscribeFxRates, unsubscribeFxRates } from './fxRateActions';

export const useMarketData = (location: LocationType, request: QuoteKey) => {
  const dispatch = useDispatch();
  const marketData = useSelector((store: IStore) => store.marketData.quotes[request.instrumentCode]);
  const [prevRequest, setPrevRequest] = useState<QuoteKey>(request);

  useEffect(() => {
    dispatch<any>(subscribeQuote(location, [request]));
    return () => dispatch<any>(unsubscribeQuote(location, [request]));
  }, []);

  useEffect(() => {
    if (prevRequest.instrumentCode !== request.instrumentCode) {
      dispatch<any>(subscribeQuote(location, [request]));

      dispatch<any>(unsubscribeQuote(location, [prevRequest]));

      setPrevRequest(request);
    }
  }, [request.instrumentCode]);

  return {
    loading: !marketData,
    polling: false,
    error: '',
    data: marketData
  };
};

export const useQuotes = (location: LocationType, requests: QuoteKey[]) => {
  const dispatch = useDispatch();
  const allQuotes = useSelector((store: IStore) => store[MARKET_DATA_STORE_NAME].quotes);
  const [prevRequests, setPrevRequests] = useState(requests);
  const [quotes, setQuotes] = useState<Record<string, IInstrumentQuote>>({});

  useEffect(() => {
    dispatch<any>(subscribeQuote(location, requests));
    return () => dispatch<any>(unsubscribeQuote(location, requests));
  }, []);

  useEffect(() => {
    if (isEqual(requests, prevRequests)) return;

    dispatch<any>(unsubscribeQuote(location, prevRequests));
    dispatch<any>(subscribeQuote(location, requests));
    setPrevRequests(requests);
    return () => dispatch<any>(unsubscribeQuote(location, requests));
  }, [requests]);

  useEffect(() => {
    const quotesArray = requests.map((request) => allQuotes[request.instrumentCode]).filter((x) => !!x);
    const quotesObject = quotesArray.reduce((prev, curr) => ({ ...prev, [curr.code]: curr }), {} as Record<InstrumentCode, IInstrumentQuote>);
    setQuotes(quotesObject);
  }, [allQuotes, requests]);

  return {
    loading: Object.keys(quotes).length === 0,
    polling: true,
    error: '',
    data: quotes
  };
};

export const useFxRates = (location: LocationType, requests: FxRateKey[]) => {
  const dispatch = useDispatch();
  const allFxRates = useSelector((store: IStore) => store[MARKET_DATA_STORE_NAME].fxRates);
  const [prevRequests, setPrevRequests] = useState(requests);
  const [fxRates, setFxRates] = useState<FxRates>({});

  useEffect(() => {
    dispatch<any>(subscribeFxRates(location, requests));
    return () => dispatch<any>(unsubscribeFxRates(location, requests));
  }, []);

  useEffect(() => {
    if (isEqual(requests, prevRequests)) return;

    dispatch<any>(unsubscribeFxRates(location, prevRequests));
    dispatch<any>(subscribeFxRates(location, requests));
    setPrevRequests(requests);
    return () => dispatch<any>(unsubscribeFxRates(location, requests));
  }, [requests]);

  useEffect(() => {
    const fxRatesObject = requests.reduce<FxRates>((prev, request) => {
      const fxRate = allFxRates[request.accountNo]?.[request.fromCurrencyCode]?.[request.toCurrencyCode];
      if (fxRate == undefined) return prev;
      // eslint-disable-next-line no-param-reassign
      const account = (prev[request.accountNo] ??= {});
      const from = (account[request.fromCurrencyCode] ??= {});
      from[request.toCurrencyCode] = fxRate;
      return prev;
    }, {});
    if (!isEqual(fxRates, fxRatesObject)) setFxRates(fxRatesObject);
  }, [allFxRates, requests]);

  return {
    loading: Object.keys(fxRates).length === 0,
    polling: true,
    error: '',
    data: fxRates
  };
};
